// Flag hands_on_board: the pure side of a video's Board (components/Board.tsx) and its Ctrl/Cmd+K command bar.
// Unit-tested in board.test.ts.
import type { Shot } from "./types";

// ------------------------------------------------------------------ the command bar

export type Command = {
  id: string;
  label: string;
  /** one short line under the label */
  hint?: string;
  /** other words it answers to ("add", "upload") */
  keywords?: string;
  disabled?: boolean;
  run: () => void;
};

const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

/** How well `c` answers `q` (0: not at all). Every word typed must be found; the label counts most. */
export function commandScore(c: Pick<Command, "label" | "hint" | "keywords">, q: string): number {
  const words = norm(q).split(" ").filter(Boolean);
  if (!words.length) return 1;
  const label = norm(c.label);
  const rest = norm(`${c.keywords ?? ""} ${c.hint ?? ""}`);
  const labelWords = label.split(" ");
  let score = 0;
  for (const w of words) {
    if (labelWords.some((lw) => lw.startsWith(w))) score += labelWords[0]!.startsWith(w) ? 4 : 3;
    else if (label.includes(w)) score += 2;
    else if (rest.split(" ").some((x) => x.startsWith(w))) score += 1;
    else return 0;
  }
  return score;
}

/** The commands that answer `q`, best first (ties keep their order); everything, in order, for an empty query. */
export function filterCommands<C extends Pick<Command, "label" | "hint" | "keywords">>(list: readonly C[], q: string, limit = 12): C[] {
  return list
    .map((c, i) => ({ c, i, s: commandScore(c, q) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.c);
}

/** Ctrl+K (Windows, Linux) or Cmd+K (Mac). */
export const isCommandKey = (e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">) => (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k";

// ------------------------------------------------------------------ the cards

export type CardTone = "off" | "draft" | "busy" | "done" | "bad";
export type CardStatus = { word: string; tone: CardTone };

/** A scene card's one-word state, from its shot and whether something is being made for it. */
export function sceneStatus(shot: Pick<Shot, "frame" | "clip"> | null | undefined, making: boolean, failed = false): CardStatus {
  if (making) return { word: "Making", tone: "busy" };
  if (failed) return { word: "Didn't work", tone: "bad" };
  const c = shot?.clip;
  const f = shot?.frame;
  if (c) return c.status === "approved" ? { word: "Clip done", tone: "done" } : { word: "Clip to check", tone: "draft" };
  if (f) return f.status === "approved" ? { word: "Frame OK", tone: "done" } : { word: "Frame to check", tone: "draft" };
  return { word: "No frame yet", tone: "off" };
}

/** What a scene card's main button does next: make the frame, approve it, or just open the scene. */
export function sceneNext(shot: Pick<Shot, "frame" | "clip"> | null | undefined, making: boolean): "frame" | "approve" | "open" {
  if (making) return "open";
  if (!shot?.frame) return "frame";
  if (shot.frame.status === "draft" && !shot.clip) return "approve";
  return "open";
}

/** A dragged library picture between cards (a place's picture onto a scene): its MIME type and payload. */
export const ASSET_DRAG = "application/x-cao-asset";
export type DraggedAsset = { assetId: string; name: string };
export function readDraggedAsset(raw: string | null | undefined): DraggedAsset | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    return typeof o.assetId === "string" && /^[A-Za-z0-9_-]{1,80}$/.test(o.assetId) ? { assetId: o.assetId, name: typeof o.name === "string" ? o.name.slice(0, 80) : "picture" } : null;
  } catch {
    return null;
  }
}
